import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { glService } from '../../services/glService';
import { coaService } from '../../services/coaService';
import { useClient } from '../../context/ClientContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { downloadTransactionDetailsPdf } from '../../utils/transactionDetailsPdf';
import JournalEntryModal from '../../components/accounting/JournalEntryModal';
import ReportLayout from '../../components/reports/ReportLayout';

const {
  FiSearch,
  FiTrash2,
  FiFileText,
  FiInfo,
  FiChevronRight,
  FiAlertCircle
} = FiIcons;

const isDebitNormal = (type) => ['Asset', 'Expense'].includes(type);

export default function GeneralLedger() {
  const { activeClient } = useClient();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [coaAccounts, setCoaAccounts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewMode, setViewMode] = useState('details');

  const [filters, setFilters] = useState({
    coaId: '',
    startDate: `${new Date().getFullYear()}-01-01`,
    endDate: new Date().toISOString().split('T')[0],
    search: '',
    reportType: 'Accrual (Paid & Unpaid)',
    contact: '',
    tag: ''
  });

  useEffect(() => {
    loadData();
  }, [activeClient.id, filters.coaId, filters.startDate, filters.endDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ledgerData, coaData] = await Promise.all([
        glService.getLedgerEntries({
          accountId: activeClient.id,
          coaId: filters.coaId,
          startDate: '',
          endDate: ''
        }),
        coaService.getChartOfAccounts(activeClient.id)
      ]);
      setEntries(ledgerData);
      setCoaAccounts(coaData);
      setExpandedGroups({});
    } catch (err) {
      console.error('Failed to load ledger:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (coaId) => {
    setExpandedGroups(prev => ({ ...prev, [coaId]: !prev[coaId] }));
  };

  const handleDelete = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this journal entry? This will remove all associated lines.')) return;
    try {
      await glService.deleteJournalEntry(batchId);
      loadData();
    } catch (err) {
      alert('Failed to delete entry');
    }
  };

  const ledgerData = useMemo(() => {
    if (!entries.length && !coaAccounts.length) {
      return { groups: [], isBalanced: true, totalDebits: 0, totalCredits: 0 };
    }

    let totalGlobalDebits = 0;
    let totalGlobalCredits = 0;
    const grouped = {};

    coaAccounts
      .filter(account => !filters.coaId || account.id === filters.coaId)
      .forEach(account => {
        grouped[account.id] = {
          account,
          allTransactions: [],
          beginningBalance: 0,
          endingBalance: 0,
          displayTransactions: []
        };
      });

    entries.forEach(tx => {
      totalGlobalDebits += Number(tx.debit) || 0;
      totalGlobalCredits += Number(tx.credit) || 0;

      if (!tx.coa) return;

      if (!grouped[tx.coa_id]) {
        grouped[tx.coa_id] = {
          account: tx.coa,
          allTransactions: [],
          beginningBalance: 0,
          endingBalance: 0,
          displayTransactions: []
        };
      }
      grouped[tx.coa_id].allTransactions.push(tx);
    });

    const start = filters.startDate ? new Date(filters.startDate) : null;
    const end = filters.endDate ? new Date(filters.endDate) : null;

    const processedGroups = Object.values(grouped)
      .map(group => {
        const debitNormal = isDebitNormal(group.account.type);
        let runningBalance = 0;
        const sortedAll = [...group.allTransactions].sort((a, b) => new Date(a.date) - new Date(b.date));
        const displayTx = [];

        sortedAll.forEach(tx => {
          const txDate = new Date(tx.date);
          const debit = Number(tx.debit) || 0;
          const credit = Number(tx.credit) || 0;

          runningBalance += debitNormal ? debit - credit : credit - debit;

          if (start && txDate < start) {
            group.beginningBalance = runningBalance;
          } else if ((!start || txDate >= start) && (!end || txDate <= end)) {
            const matchesSearch = !filters.search ||
              tx.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
              tx.coa?.name.toLowerCase().includes(filters.search.toLowerCase()) ||
              tx.reference_type?.toLowerCase().includes(filters.search.toLowerCase());
            const matchesContact = !filters.contact ||
              tx.payee?.toLowerCase().includes(filters.contact.toLowerCase());
            const matchesTag = !filters.tag ||
              tx.reference_type?.toLowerCase() === filters.tag.toLowerCase();

            if (matchesSearch && matchesContact && matchesTag) {
              displayTx.push({ ...tx, runningBalance });
            }
          }
        });

        group.endingBalance = runningBalance;
        group.displayTransactions = displayTx;
        return group;
      })
      .filter(group => group.displayTransactions.length > 0);

    processedGroups.sort((a, b) => (a.account.code || '').localeCompare(b.account.code || ''));
    return {
      groups: processedGroups,
      isBalanced: Math.abs(totalGlobalDebits - totalGlobalCredits) < 0.01,
      totalDebits: totalGlobalDebits,
      totalCredits: totalGlobalCredits
    };
  }, [entries, coaAccounts, filters]);

  const getGroupsToExport = () => ledgerData.groups.filter(group => (
    group.displayTransactions.length > 0 ||
    group.beginningBalance !== 0 ||
    group.endingBalance !== 0
  ));

  const getExportFileName = () => `${activeClient?.name || 'Business'} - Transaction Details`;

  const handleExport = () => {
    const groupsToExport = getGroupsToExport();

    if (groupsToExport.length === 0) {
      alert('No data to export');
      return;
    }

    const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const formatAmount = (value) => {
      const amount = Number(value) || 0;
      return amount === 0 ? '' : amount.toFixed(2);
    };
    const formatBalance = (value) => (Number(value) || 0).toFixed(2);
    const organizedRows = [
      ['Statement of Transactions Details'],
      [activeClient?.name || 'Business'],
      ['For period', `${filters.startDate || 'All dates'} to ${filters.endDate || 'Current date'}`],
      []
    ];

    groupsToExport.forEach((group, index) => {
      if (index > 0) organizedRows.push([]);

      organizedRows.push([
        [group.account.code, group.account.name].filter(Boolean).join(' - ')
      ]);
      organizedRows.push(['Date', 'Description', 'Debit', 'Credit', 'Balance']);
      organizedRows.push(['', 'Beginning Balance', '', '', formatBalance(group.beginningBalance)]);

      group.displayTransactions.forEach(entry => {
        organizedRows.push([
          entry.date || '',
          entry.description || 'System Entry',
          formatAmount(entry.debit),
          formatAmount(entry.credit),
          formatBalance(entry.runningBalance)
        ]);
      });

      organizedRows.push(['', 'Ending Balance', '', '', formatBalance(group.endingBalance)]);
    });

    const organizedCsvContent = `\ufeff${organizedRows
      .map(row => row.map(escapeCell).join(','))
      .join('\r\n')}`;
    const organizedBlob = new Blob([organizedCsvContent], { type: 'text/csv;charset=utf-8;' });
    const organizedLink = document.createElement('a');
    const organizedUrl = URL.createObjectURL(organizedBlob);

    organizedLink.href = organizedUrl;
    organizedLink.download = `${getExportFileName()}.csv`;
    organizedLink.style.visibility = 'hidden';
    document.body.appendChild(organizedLink);
    organizedLink.click();
    organizedLink.remove();
    URL.revokeObjectURL(organizedUrl);
    setIsExportOpen(false);
  };

  const handleExportPdf = async () => {
    const groupsToExport = getGroupsToExport();

    if (groupsToExport.length === 0) {
      alert('No data to export');
      return;
    }

    setIsExportOpen(false);

    try {
      await downloadTransactionDetailsPdf({
        groups: groupsToExport,
        businessName: activeClient?.name || 'Business',
        fileName: getExportFileName(),
        periodStart: filters.startDate ? formatDate(filters.startDate) : 'All dates',
        periodEnd: filters.endDate ? formatDate(filters.endDate) : 'Current date',
        formatCurrency,
        formatDate
      });
    } catch (error) {
      console.error('Failed to download Transaction Details PDF:', error);
      alert(error.message || 'Unable to generate this PDF. Please try again.');
    }
  };

  const handleReportExport = (format) => {
    if (format === 'pdf') {
      handleExportPdf();
      return;
    }

    handleExport();
  };

  const accountTypes = [
    'Asset',
    'Liability',
    'Equity',
    'Income',
    'Revenue',
    'Expense',
    'Expenses'
  ];

  const accountOptions = (
    <>
      <option value="">All Accounts</option>
      {accountTypes
        .filter(type => coaAccounts.some(acc => acc.type === type))
        .map(type => (
          <optgroup key={type} label={type}>
            {[...coaAccounts]
              .filter(acc => acc.type === type)
              .sort((a, b) => (
                (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }) ||
                (a.name || '').localeCompare(b.name || '')
              ))
              .map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </option>
              ))}
          </optgroup>
        ))}
      {Object.keys(
        coaAccounts
          .filter(acc => !accountTypes.includes(acc.type))
          .reduce((groups, acc) => {
            const type = acc.type || 'Other Accounts';
            if (!groups[type]) groups[type] = [];
            groups[type].push(acc);
            return groups;
          }, {})
      ).sort().map(type => (
        <optgroup key={type} label={type}>
          {coaAccounts
            .filter(acc => (acc.type || 'Other Accounts') === type)
            .sort((a, b) => (
              (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }) ||
              (a.name || '').localeCompare(b.name || '')
            ))
            .map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </option>
            ))}
        </optgroup>
      ))}
    </>
  );

  const ledgerControls = (
    <>
      <label className="relative flex items-center">
        <SafeIcon
          icon={FiSearch}
          className="pointer-events-none absolute left-2.5 text-sm text-slate-400"
        />
        <input
          type="search"
          aria-label="Search ledger"
          placeholder="Search ledger"
          value={filters.search}
          onChange={(event) => setFilters({ ...filters, search: event.target.value })}
          className="w-40 rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-2 text-xs text-slate-700 outline-none transition focus:border-blue-400"
        />
      </label>

      <select
        aria-label="Filter by account"
        value={filters.coaId}
        onChange={(event) => setFilters({ ...filters, coaId: event.target.value })}
        className="max-w-40 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition focus:border-blue-400"
      >
        {accountOptions}
      </select>

      <select
        aria-label="Report type"
        value={filters.reportType}
        onChange={(event) => setFilters({ ...filters, reportType: event.target.value })}
        className="max-w-44 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition focus:border-blue-400"
      >
        <option>Accrual (Paid & Unpaid)</option>
        <option>Cash Basis</option>
        <option>Cash (Paid)</option>
        <option>Cash Only</option>
      </select>

      <input
        type="search"
        aria-label="Filter by contact"
        placeholder="All Contacts"
        value={filters.contact}
        onChange={(event) => setFilters({ ...filters, contact: event.target.value })}
        className="w-32 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition focus:border-blue-400"
      />
    </>
  );

  return (
    <ReportLayout
      title="Transaction Details"
      description="Review posted ledger activity by account, period, and transaction context."
      viewMode={viewMode}
      setViewMode={setViewMode}
      startDate={filters.startDate}
      endDate={filters.endDate}
      onStartDateChange={(value) => setFilters({ ...filters, startDate: value })}
      onEndDateChange={(value) => setFilters({ ...filters, endDate: value })}
      onRefresh={loadData}
      onExport={handleReportExport}
      loading={loading}
      showTabs={false}
      exportOpen={isExportOpen}
      setExportOpen={setIsExportOpen}
      additionalControls={ledgerControls}
    >
      <div className="general-ledger-compact max-w-[1600px] mx-auto space-y-10 pb-20">
        <style>{`
          .general-ledger-compact {
            padding-bottom: 2.5rem;
          }

          .general-ledger-compact > * + * {
            margin-top: 1.5rem;
          }

          .general-ledger-compact .font-black {
            font-weight: 600;
          }

          .general-ledger-compact .font-bold {
            font-weight: 500;
          }

          .general-ledger-compact .rounded-\\[32px\\] {
            border-radius: 1.25rem;
          }

          .general-ledger-compact .p-20 {
            padding: 3.5rem;
          }

          .general-ledger-compact .p-10 {
            padding: 1.5rem;
          }

          .general-ledger-compact .p-8 {
            padding: 1.25rem;
          }

          .general-ledger-compact .px-10 {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }

          .general-ledger-compact .py-8 {
            padding-top: 1rem;
            padding-bottom: 1rem;
          }

          .general-ledger-compact .py-6 {
            padding-top: 0.875rem;
            padding-bottom: 0.875rem;
          }

          .general-ledger-compact .pb-8 {
            padding-bottom: 1rem;
          }

          .general-ledger-compact .gap-16 {
            gap: 2.5rem;
          }

          .general-ledger-compact .gap-12 {
            gap: 2.5rem;
          }

          .general-ledger-compact .text-4xl {
            font-size: 2rem;
            line-height: 2.25rem;
          }

          .general-ledger-compact .text-2xl {
            font-size: 1.25rem;
            line-height: 1.75rem;
          }
        `}</style>

        <div className="space-y-6">
          {loading ? (
            <div className="bg-white rounded-[32px] p-20 text-center flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin" />
              <p className="text-slate-400 font-black uppercase tracking-widest text-[10px]">
                Syncing Ledger...
              </p>
            </div>
          ) : ledgerData.groups.length === 0 ? (
            <div className="bg-white rounded-[32px] p-20 text-center flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiSearch} className="text-2xl text-slate-300" />
              </div>
              <p className="text-slate-900 font-black text-lg tracking-tight">No records found</p>
              <p className="text-slate-400 font-bold max-w-xs mx-auto">
                Try adjusting your filters or date range.
              </p>
            </div>
          ) : (
            ledgerData.groups.map(group => {
              const isExpanded = expandedGroups[group.account.id];

              return (
                <motion.div
                  layout
                  key={group.account.id}
                  className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden"
                >
                  <div
                    onClick={() => toggleGroup(group.account.id)}
                    className="px-10 py-8 flex items-center justify-between cursor-pointer hover:bg-slate-50/50 transition-colors group"
                  >
                    <div className="flex items-center gap-6">
                      <div className={`p-2 rounded-xl bg-slate-50 text-slate-400 transition-transform ${isExpanded ? 'rotate-90 bg-slate-900 text-white' : 'group-hover:bg-slate-100'}`}>
                        <SafeIcon icon={FiChevronRight} className="text-lg" />
                      </div>
                      <div>
                        <h2 className="text-xl font-black text-slate-900 tracking-tight">
                          {group.account.code} • {group.account.name}
                        </h2>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                            {group.account.type}
                          </span>
                          <span className="w-1 h-1 rounded-full bg-slate-200" />
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            {group.displayTransactions.length} entries
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-16">
                      <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          Beginning
                        </p>
                        <p className="text-lg font-black text-slate-900 mt-1">
                          {formatCurrency(group.beginningBalance)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          Ending
                        </p>
                        <p className={`text-lg font-black mt-1 ${group.endingBalance < 0 ? 'text-rose-600' : 'text-blue-600'}`}>
                          {formatCurrency(group.endingBalance)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 1 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-slate-50 overflow-hidden"
                      >
                        <div className="px-10 pb-8 pt-2 overflow-x-auto">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="border-b border-slate-50">
                                <th className="py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest w-32">
                                  Date
                                </th>
                                <th className="py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                  Description & Reference
                                </th>
                                <th className="py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right w-40">
                                  Debit (+)
                                </th>
                                <th className="py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right w-40">
                                  Credit (-)
                                </th>
                                <th className="py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right w-40">
                                  Running Balance
                                </th>
                                <th className="py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center w-20">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                              {group.displayTransactions.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="py-12 text-center text-sm font-bold text-slate-400 italic">
                                    No transactions recorded in this period.
                                  </td>
                                </tr>
                              ) : (
                                group.displayTransactions.map(tx => (
                                  <tr key={tx.id} className="group/row hover:bg-slate-50/50 transition-all">
                                    <td className="py-6 text-[13px] font-black text-slate-900">
                                      {formatDate(tx.date)}
                                    </td>
                                    <td className="py-6 pr-6">
                                      <div className="flex items-center gap-2">
                                        <p className="text-[13px] font-bold text-slate-900">
                                          {tx.description || 'System Entry'}
                                        </p>
                                        {tx.is_manual && (
                                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-black uppercase tracking-widest rounded-md">
                                            Manual
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                                        {tx.reference_type} • {tx.batch_id?.split('-')[0] || tx.id.split('-')[0]}
                                      </p>
                                    </td>
                                    <td className="py-6 text-[13px] font-mono text-right text-slate-900">
                                      {tx.debit > 0 ? formatCurrency(tx.debit) : '—'}
                                    </td>
                                    <td className="py-6 text-[13px] font-mono text-right text-slate-900">
                                      {tx.credit > 0 ? formatCurrency(tx.credit) : '—'}
                                    </td>
                                    <td className="py-6 text-[13px] font-mono font-black text-right text-slate-900 bg-slate-50/30">
                                      {formatCurrency(tx.runningBalance)}
                                    </td>
                                    <td className="py-6">
                                      <div className="flex items-center justify-center gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity">
                                        {tx.is_manual && (
                                          <button
                                            onClick={() => handleDelete(tx.batchId)}
                                            className="p-2 text-slate-400 hover:text-rose-500 hover:bg-white hover:shadow-sm rounded-lg transition-all"
                                            title="Delete Journal Entry"
                                          >
                                            <SafeIcon icon={FiTrash2} />
                                          </button>
                                        )}
                                        <button className="p-2 text-slate-400 hover:text-slate-900 hover:bg-white hover:shadow-sm rounded-lg transition-all">
                                          <SafeIcon icon={FiInfo} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })
          )}
        </div>

        {!loading && ledgerData.groups.length > 0 && (
          <div className="bg-slate-900 rounded-[32px] p-10 flex flex-col md:flex-row items-center justify-between gap-8 text-white shadow-2xl shadow-slate-300">
            <div className="flex items-center gap-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${ledgerData.isBalanced ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                <SafeIcon icon={ledgerData.isBalanced ? FiFileText : FiAlertCircle} className="text-2xl" />
              </div>
              <div>
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                  Ledger Balance Status
                </p>
                <h3 className="text-2xl font-black tracking-tight mt-1">
                  {ledgerData.isBalanced ? 'Perfectly Balanced' : 'Out of Balance'}
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-12">
              <div className="text-right">
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                  Total Debits
                </p>
                <p className="text-2xl font-mono font-black mt-1">
                  {formatCurrency(ledgerData.totalDebits)}
                </p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div className="text-right">
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                  Total Credits
                </p>
                <p className="text-2xl font-mono font-black mt-1">
                  {formatCurrency(ledgerData.totalCredits)}
                </p>
              </div>
            </div>
          </div>
        )}

        <JournalEntryModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={loadData}
        />
      </div>
    </ReportLayout>
  );
}